import React, { useEffect, useState, useRef, useMemo } from 'react'
import { connect, useDispatch, useSelector } from 'react-redux'
import { withRouter } from 'react-router-dom'
import cn from 'classnames'
import { get, map, isNumber, findIndex, isEmpty } from 'lodash'
import CanvasDraw from 'react-canvas-draw'
import useDeepCompareEffect from 'use-deep-compare-effect'

import GratuityHeader from '../components/GratuityHeader'
import Button from '../components/Button'
import SegmentedControl from '../components/SegmentedControl'
import TipEntryModal from '../components/TipEntryModal'
import ConfirmModalV2 from '../components/ConfirmModalV2'
import BackButton from '../kiosk/components/BackButton'
import OfflineModal from '../kiosk/OfflineModal'

import { makeGetCurrentMenu, getIsKiosk, getRoundUpForCharityFeatureEnabled } from '../selectors/menus'
import {
  setKioskPayment,
  closeNoOpTender,
  addTipOrderInProgress,
  clearOrderInProgress,
  addPaymentToOrderInProgress,
  updateSavedPayment,
  clearTipOrderInProgress,
  clearTipFromOrder,
  removePaymentsFromOrderInProgress,
  addDonationsToOrderInProgress,
  clearDonationOrderInProgress,
  clearDonationFromOrder,
  selectOrder,
  orderDetailsRequested,
} from '../actions/order'
import { KIOSK_PAYMENT_TYPES, PAYMENT_TYPES } from '../utils/paymentTypes'
import { KEY_CODES } from '../kiosk/utils/contants'
import makeTipSuggestions from '../utils/makeTipSuggestions'
import styles from './Signature.module.scss'

import { getCartItemsCountWithQuantities, getSignatureDefaults, getSubtotal, getTotal } from '../selectors/cart'
import { getKioskPayment, getOrderInProgress } from '../selectors/order'
import { getDefaultTipEnabled } from '../selectors/revenueCenter'
import useRemoteOrderTotal from '../hooks/useRemoteOrderTotal'
import { ReactComponent as BlackX } from '../assets/icons/signature-x.svg'
import { ReactComponent as KioskX } from '../assets/icons/black-x.svg'
import { SIGNATURE_THRESHOLD_AMOUNT } from '../constants'
import ConnectingIndicator from '../components/ConnectingIndicator'
import useCreateOrderInProgress from '../hooks/useCreateOrderInProgress'
import VNConcessionsNavBar from '../components/VNConcessionsNavBar'
import useGetIsMobileScreen from '../hooks/useGetIsMobileScreen'
import VNBottomButton from '../components/VNBottomButton'
import { isAuthFailedTab, isTabbed } from '../utils/orderStates'
import { currentOrderUuid } from '../utils/orderNumbers'
import useCloseFreeOrder from '../hooks/useCloseFreeOrder'
import { ToastManager } from '../utils/toastManager'
import { centsToDollars } from '../utils/formatters'
import useSpinner from '../context/hooks/useSpinner'
import { Charity } from './Charity'
import { KioskCharity } from '../kiosk/Charity'
import { setSignatureDefaults } from '../actions/cart'
import { getBackbarMode, getNetworkAvailable } from '../selectors/config'
import { Box } from '@material-ui/core'
import { VNButtonContainer } from '../VNCustomerFacingDisplay/components/VNButtonContainer'
import useTaxResults from '../VNTaxes/hooks/useTaxResults'
import { VNTotalListItem } from '../VNCustomerFacingDisplay/components/VNTotalListItem'
import { makeGetServiceChargeLegalText } from '../selectors/venue'
import { Info } from '@material-ui/icons'
import VNLoader from '../VNLoader/components/VNLoader'
import { setConnectionRequiredDialog } from '../VNDialogs/ActionCreators'
import { APPLICATION_STRINGS } from '../strings'
import { getIsOrderTaxExempt } from '../VNTaxes/Selectors'
import { getKioskOfflineEnabled } from '../kiosk/utils/config'

const isUsableNumber = (value) => isNumber(value) && value > 0

const Signature = ({
  itemCount,
  addTipToOrderInProgress,
  addDonationToOrderInProgress,
  addPaymentToSavedCard,
  confirmDefaultTipEnabled,
  className,
  closeNoOpTender,
  closeWithSavedCard,
  customTender,
  goPrevScreen,
  history,
  isKiosk,
  kioskPayment,
  maxGratuity,
  menuTipSuggestions,
  offlineTotal,
  order,
  paymentId,
  setKioskPayment,
  tenderAmountInCents,
  addTipThenRedirectPath,
  maximumAmount,
  addPaymentToOrder,
  showCharityFirst,
  skipTips,
  addSignatureDefaults,
  signatureDefaults,
  serviceChargeLegal,
}) => {
  const classNames = cn(styles.signature, isKiosk ? styles.signatureKiosk : null, className)
  const signaturePadRef = useRef(null)
  const intervalRef = useRef(null)
  const backButtonRef = useRef()
  const dispatch = useDispatch()
  const [selectedTipSuggestionIndex, setSelectedTipSuggestionIndex] = useState()
  const [selectedTipAmount, setSelectedTipAmount] = useState(signatureDefaults?.selectedTipAmount || 0)
  const [isSignatureValid, setIsSignatureValid] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const [newLinesMatch, setNewLinesMatch] = useState(false)
  const [shouldAnimate, setShouldAnimate] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [showCustomTipModal, setShowCustomTipModal] = useState(false)
  const [showConfirmDefaultTipModal, setConfirmDefaultTipModal] = useState(false)
  const [selectionIsDefault, setSelectionIsDefault] = useState(true)
  const isTabAuthFailed = isAuthFailedTab(order)
  const [orderUuid, setOrderUuid] = useState(isTabAuthFailed ? order?.uuid : currentOrderUuid())
  const [createOrder, orderCreated] = useCreateOrderInProgress()
  const [isSubmitted, setIsSubmitted] = useState(false)
  const offlineSubtotal = useSelector(getSubtotal)
  const isBackBar = useSelector(getBackbarMode)
  const taxExempt = useSelector(getIsOrderTaxExempt)
  const { closeFreeOrder } = useCloseFreeOrder()
  const [donating, setDonating] = useState(false)
  const [savedSignature, setSavedSignature] = useState(null)
  const donationAmountInCents = useRef(0)
  const previousLines = useRef(0)

  const [shouldNavigateToSplit, setShouldNavigateToSplit] = useState(false)

  const [loadRemoteOrderTotal, { data: remoteOrderTotal, loading, success, failed }] = useRemoteOrderTotal()
  const isDoneLoading = (paymentId || !isEmpty(order) || (!loading && (failed || success))) && !isSubmitted
  const tipAmountInCents = selectedTipAmount || 0
  let customTipModalElement = null
  let confirmDefaultTipModalElement = null
  const isTabOrder = isTabbed(order)
  const networkAvailable = useSelector(getNetworkAvailable)
  const isMobile = useGetIsMobileScreen()
  const { taxRulesBreakdown } = useTaxResults()

  let subtotalInCents = order?.subtotalAmountInCents ?? remoteOrderTotal?.subtotalInCents ?? offlineSubtotal
  let totalInCents = order?.amountInCents ?? remoteOrderTotal?.totalAmountInCents ?? offlineTotal

  // use offline totals if failed or offline, but these come from cart items, which does not apply for tabs
  if ((failed || !networkAvailable) && !order.isTab) {
    totalInCents = offlineTotal
  }

  // If the order is 0 cents due to a discount, set the subtotal also to 0 cents so that tip options display appropiately
  if (totalInCents === 0) {
    subtotalInCents = 0
  }

  // if tab order or split-pay flow, use tenderAmountInCents as total
  if ((isTabOrder && !addTipThenRedirectPath) || paymentId) {
    totalInCents = tenderAmountInCents
  }

  const [tipSuggestions, setTipSuggestions] = useState(
    makeTipSuggestions(remoteOrderTotal?.tipSuggestions ?? menuTipSuggestions, subtotalInCents),
  )
  const total = `$${(totalInCents / 100).toFixed(2)}`
  const tip = `$${(tipAmountInCents / 100).toFixed(2)}`
  const grandTotalInCents = totalInCents + tipAmountInCents
  const grandTotal = `$${(grandTotalInCents / 100).toFixed(2)}`
  const noPaymentRequired = grandTotalInCents === 0
  const signatureRequired =
    !isMobile &&
    isEmpty(customTender) &&
    !addTipThenRedirectPath &&
    !!(
      (SIGNATURE_THRESHOLD_AMOUNT === 0 && !noPaymentRequired) ||
      (SIGNATURE_THRESHOLD_AMOUNT && grandTotalInCents >= SIGNATURE_THRESHOLD_AMOUNT)
    )
  const isFormValid = signatureRequired ? isSignatureValid : true
  const tipSuggestionItems = map(tipSuggestions, 'display')
  const initialTipPercentage = get(tipSuggestions, selectedTipSuggestionIndex)?.display
  const roundUpForCharityEnabled = useSelector((state) => getRoundUpForCharityFeatureEnabled(state, grandTotalInCents))
  const { showSpinner, stopSpinner } = useSpinner()
  const kioskOfflineEnabled = getKioskOfflineEnabled({ fromAttractLoop: false })

  // used when returning to the signature screen from card reader or other tenders
  const initialSelectedIndex = useMemo(() => {
    const selectedIndex = findIndex(tipSuggestions, 'selected')
    if (isNumber(selectedIndex) && selectedIndex >= 0) {
      return selectedIndex
    }
    return signatureDefaults?.selectedTipIndex ?? selectedIndex
  }, [tipSuggestions, signatureDefaults])

  const shouldShowDonation = useMemo(() => {
    return (!order?.hasTipApplied || showCharityFirst) && roundUpForCharityEnabled && isEmpty(customTender) && !isBackBar
  }, [order, roundUpForCharityEnabled, customTender, isBackBar, showCharityFirst])

  const placeFreeOrder = (orderUuid, isKiosk, isTabOrder) => {
    closeFreeOrder(orderUuid, isTabOrder)
    if (isKiosk) {
      history.push(`/kiosk/order/summary/${orderUuid}`)
    }
  }

  useEffect(() => {
    const { creditCard, mobileWallet } = KIOSK_PAYMENT_TYPES

    if (signatureDefaults?.signature) {
      setSavedSignature(signatureDefaults.signature)
    }

    if (showSpinner) {
      stopSpinner()
    }

    // prevent new order uuid from being set if order is already created
    if (isTabAuthFailed) {
      return
    }

    if (!paymentId && !addTipThenRedirectPath && !isTabOrder) {
      loadRemoteOrderTotal()
    }
    if (addTipThenRedirectPath && !isTabOrder) {
      createOrder()
    }
    if (isKiosk && kioskPayment.paymentType === mobileWallet) {
      setKioskPayment(creditCard)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // check for empty tipSuggestions so only placeFreeOrder for orders totaling 0 with no suggestions.
  // We want users to be able to tip on orders totaling zero, but only for tip enabled menus
  useEffect(() => {
    if (!isDoneLoading) {
      return
    }

    if (totalInCents === 0 && isEmpty(tipSuggestions)) {
      placeFreeOrder(orderUuid, isKiosk, isTabOrder)
    } else if (!signatureRequired && isEmpty(tipSuggestions) && !addTipThenRedirectPath && !skipTips) {
      setIsReady(true)
    } else if (totalInCents !== offlineTotal || remoteOrderTotal?.tipSuggestions) {
      setTipSuggestions(makeTipSuggestions(remoteOrderTotal?.tipSuggestions ?? menuTipSuggestions, subtotalInCents))
    }

    if (shouldNavigateToSplit) {
      // add tip and donation before adding payment to order
      addDonationToOrderInProgress(donationAmountInCents.current)
      addTipToOrderInProgress(tipAmountInCents)
      // add signature then apply payment to order
      addSignatureDefaults(selectedTipSuggestionIndex, selectedTipAmount, shouldShowDonation, savedSignature)
      addPaymentToOrder({ customTender, tenderAmountInCents: maximumAmount, paymentId, maximumAmount })
      ToastManager.success(
        `${centsToDollars(maximumAmount)} applied. ${centsToDollars(tenderAmountInCents + tipAmountInCents - maximumAmount)} balance due.`,
        { autoClose: true },
      )
      history.push({
        pathname: '/tender-selection',
        state: {
          previousPaymentType: PAYMENT_TYPES.FIXED_CUSTOM_TENDER,
          tenderAmountInCents: tenderAmountInCents + tipAmountInCents - maximumAmount,
          fromSplit: true,
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, success, failed, orderCreated, shouldNavigateToSplit])

  useEffect(() => {
    if (showCharityFirst) {
      setDonating(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (remoteOrderTotal?.uuid && success) {
      setOrderUuid(remoteOrderTotal.uuid)
    }
  }, [remoteOrderTotal?.uuid, success])

  useEffect(() => {
    if (isDoneLoading && backButtonRef.current) backButtonRef.current.focus()
  }, [isDoneLoading])

  useEffect(() => {
    if (!selectedTipAmount) {
      setSelectedTipAmount(0)
    }
  }, [selectedTipAmount])

  useEffect(() => {
    if (isReady && signatureRequired) {
      intervalRef.current = setInterval(() => {
        if (newLinesMatch) {
          return
        }

        doLinesMatch()
      }, 10)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, newLinesMatch, signatureRequired])

  useDeepCompareEffect(() => {
    setSelectedTipSuggestionIndex(initialSelectedIndex)
    setSelectionIsDefault(signatureDefaults?.selectedTipIndex === null)

    if (initialSelectedIndex !== 0 && !initialSelectedIndex) {
      setSelectedTipAmount(undefined)
      return
    }

    if (signatureDefaults?.selectedTipAmount) {
      setSelectedTipAmount(signatureDefaults.selectedTipAmount)
      return
    }

    const initialSelectedTipSuggestion = get(tipSuggestions, `[${initialSelectedIndex}]`, {})
    const initialSelectedTipAmount = get(initialSelectedTipSuggestion, 'amountInCents', 0)
    setSelectedTipAmount(initialSelectedTipAmount)
  }, [tipSuggestions])

  useEffect(() => () => clearInterval(intervalRef.current))

  if (showCustomTipModal) {
    customTipModalElement = (
      <TipEntryModal
        key="custom"
        title="Custom Tip"
        isKiosk={isKiosk}
        initialValue={selectedTipAmount}
        saveButtonText="Done"
        onClose={() => setShowCustomTipModal(false)}
        onSubmit={(tipAmountInCents) => handleSetNewTip(tipAmountInCents)}
      />
    )
  }

  if (showConfirmDefaultTipModal) {
    confirmDefaultTipModalElement = (
      <ConfirmModalV2
        onButtonOneClick={() => setConfirmDefaultTipModal(false)}
        onButtonTwoClick={() => {
          setConfirmDefaultTipModal(false)

          if (roundUpForCharityEnabled) {
            // setup charity screen and display it
            onNextButtonClick(null, true)
            return
          }

          onNext()
        }}
        headerText={`Confirm ${initialTipPercentage} Suggested Tip`}
        subtext={'Our staff appreciates your generosity'}
        isKiosk={isKiosk}
        buttonOneText={'CANCEL'}
        buttonTwoText={'CONFIRM'}
      />
    )
  }

  const handleSetNewTip = (tipAmountInCents) => {
    const newTipAmount = isUsableNumber(maxGratuity) ? Math.min(maxGratuity, tipAmountInCents) : tipAmountInCents

    setSelectedTipAmount(newTipAmount)
    triggerPopAnimation()
  }

  const triggerPopAnimation = () => {
    setShouldAnimate(true)
    setTimeout(() => setShouldAnimate(false), 100)
  }

  const selectedTipSuggestionDidChange = (selectedIndex) => {
    const selectedSuggestion = get(tipSuggestions, `[${selectedIndex}]`, {})
    const selectionIsSame = selectedIndex === selectedTipSuggestionIndex
    const resolvedTipAmount = selectionIsSame ? selectedTipAmount : selectedSuggestion.amountInCents

    if (selectedSuggestion.custom) {
      setShowCustomTipModal(true)
    }

    if (!selectionIsSame) {
      setSelectionIsDefault(false)
    }

    if (!selectedSuggestion.custom && !selectionIsSame && !shouldAnimate) {
      triggerPopAnimation()
    }

    setSelectedTipSuggestionIndex(selectedIndex)
    setSelectedTipAmount(resolvedTipAmount)
  }

  const completeSignature = (isReady = true) => {
    const save = signaturePadRef.current.getSaveData()
    const nextDrawing = save.replace(/#FFFFFF/gim, '#000000')
    signaturePadRef.current.loadSaveData(nextDrawing, true)
    previousLines.current = JSON.parse(save).lines.length
    const signature = signaturePadRef?.current?.canvas?.drawing?.toDataURL('image/svg+xml')
    setSavedSignature(signature)
    addSignatureDefaults(selectedTipSuggestionIndex, selectedTipAmount, shouldShowDonation, signature)
    setIsReady(isReady)
  }

  const doLinesMatch = () => {
    if (!signaturePadRef?.current && signatureDefaults?.signature) {
      setNewLinesMatch(true)
      return
    }

    const newLinesDrawn = JSON.parse(signaturePadRef?.current?.getSaveData()).lines.length

    if (newLinesDrawn === previousLines.current) {
      setNewLinesMatch(true)
    }
  }

  const completeTipping = () => {
    const drawing = signatureRequired ? get(signaturePadRef, 'current.canvas.drawing') : null
    const signature = drawing ? (drawing.toDataURL('image/svg+xml') ?? savedSignature) : null

    if (closeWithSavedCard) {
      addPaymentToSavedCard(tipAmountInCents, donationAmountInCents.current, signature)
      return
    }
    if (noPaymentRequired) {
      placeFreeOrder(orderUuid, isKiosk, isTabOrder)
    } else if (addTipThenRedirectPath) {
      addSignatureDefaults(selectedTipSuggestionIndex, selectedTipAmount, shouldShowDonation, savedSignature)
      addDonationToOrderInProgress(donationAmountInCents.current)
      addTipToOrderInProgress(tipAmountInCents, addTipThenRedirectPath)
    } else if (!isEmpty(customTender)) {
      if (maximumAmount !== null && tenderAmountInCents + tipAmountInCents + donationAmountInCents.current > maximumAmount) {
        createOrder()
        setShouldNavigateToSplit(true)
      } else {
        closeNoOpTender({
          order,
          orderUuid,
          customTender,
          tipAmountInCents,
          tenderAmountInCents,
          charityAmountInCents: donationAmountInCents.current,
          paymentId,
          redirectOnSync: true,
        })
        setIsSubmitted(true)
      }
    } else if (isKiosk) {
      addSignatureDefaults(selectedTipSuggestionIndex, selectedTipAmount, shouldShowDonation, savedSignature)
      history.push({
        pathname: `/kiosk/order/payment`,
        state: { tipAmountInCents, charityAmountInCents: donationAmountInCents.current },
      })
    } else {
      setIsReady(true)
    }
  }

  const clearSignature = () => {
    signaturePadRef?.current?.clear()
    setIsSignatureValid(false)
  }

  useEffect(() => {
    if (isReady && !processing && (newLinesMatch || !signatureRequired)) {
      clearInterval(intervalRef?.current)

      if (noPaymentRequired) {
        placeFreeOrder(orderUuid, isKiosk, isTabOrder)
      }

      let signature = ''
      if (signatureRequired) {
        const drawing = get(signaturePadRef, 'current.canvas.drawing')
        signature = drawing ? drawing.toDataURL('image/svg+xml') : 'Signature Error'
        if (savedSignature) {
          signature = savedSignature
        }
      }

      if (closeWithSavedCard) {
        setProcessing(true)
        addPaymentToSavedCard(tipAmountInCents, donationAmountInCents.current, signature)
      }

      if (isKiosk && !noPaymentRequired) {
        addSignatureDefaults(selectedTipSuggestionIndex, selectedTipAmount, shouldShowDonation, savedSignature)
        history.push({
          pathname: `/kiosk/order/payment`,
          state: { receiptSignature: signature, tipAmountInCents, charityAmountInCents: donationAmountInCents.current },
        })
      }

      // don't redirect if it's custom tenders
      if (customTender?.name) {
        return
      }

      if (!isKiosk && !closeWithSavedCard) {
        addSignatureDefaults(selectedTipSuggestionIndex, selectedTipAmount, shouldShowDonation, savedSignature)
        const orderUuid = order?.uuid

        history.push({
          pathname: `/card-reader`,
          state: {
            orderUuid,
            selectedTipAmount,
            donationAmountInCents: donationAmountInCents.current,
            remoteOrderTotal,
            isKiosk,
            paymentId,
            tenderAmountInCents,
            offlineTotal,
            signature,
          },
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, newLinesMatch, signatureRequired])

  const clientWidth = document?.body?.clientWidth
  const clientHeight = document?.body?.clientHeight
  const canvasWidth = clientWidth < 450 || isKiosk ? clientWidth * 0.9 : clientWidth * 0.6 || 500
  const getResponsiveCanvasHeight = () => {
    let canvasHeight = clientHeight * 0.445

    if (isMobile && clientHeight <= 640) {
      canvasHeight = clientHeight * 0.33
    }

    if (isMobile && !isEmpty(tipSuggestions)) {
      canvasHeight = clientHeight * 0.36
    }

    if (isKiosk) {
      canvasHeight = clientHeight * 0.31
    }

    return canvasHeight
  }

  let primaryText = !isDoneLoading ? '-' : grandTotal
  let secondaryTextKiosk = !isDoneLoading ? '-' : `${total} + ${tip} Tip`
  let secondaryText = !isKiosk ? (tipAmountInCents > 0 ? `${total} + ${tip} tip` : 'Add a tip') : secondaryTextKiosk

  const handleKeyDown = (e) => {
    if (e.keyCode === KEY_CODES.UP) {
      if (isKiosk) {
        addSignatureDefaults(null, null, false, null)
        history.push('/kiosk/order/new')
      } else {
        goPrevScreen(order)
      }
    }
  }

  const SignatureIcon = () => {
    if (isKiosk) {
      return <KioskX onClick={clearSignature} />
    }

    return <BlackX onClick={clearSignature} />
  }

  const goBack = () => {
    addSignatureDefaults(null, null, false, null)
    isKiosk ? history.push('/kiosk/order/new') : goPrevScreen(order)
  }

  const onNext =
    (SIGNATURE_THRESHOLD_AMOUNT || SIGNATURE_THRESHOLD_AMOUNT === 0) && signatureRequired ? completeSignature : completeTipping

  const containerStyle = {
    marginTop:
      signatureRequired && isEmpty(tipSuggestions) && !isMobile && !isKiosk
        ? '10vh'
        : addTipThenRedirectPath === '/split-tender' || (!addTipThenRedirectPath && isEmpty(customTender))
          ? '2.037vh'
          : '4.259vh',
    height: !signatureRequired && !isMobile ? '37%' : '',
    justifyContent: !signatureRequired ? 'space-between' : '',
  }

  if (isKiosk && (donating || skipTips)) {
    return (
      <>
        <KioskCharity
          totalInCents={grandTotalInCents}
          onDone={(donationAmount) => {
            donationAmountInCents.current = donationAmount
            if (signatureRequired) {
              setIsReady(true)
            } else {
              onNext()
            }
          }}
          onBack={() => {
            if (skipTips) {
              history.push('/kiosk/order/new')
            }
            setDonating(false)
            donationAmountInCents.current = 0
          }}
        />
        {customTipModalElement}
        {confirmDefaultTipModalElement}
      </>
    )
  }

  if (donating) {
    return (
      <>
        <Charity
          totalInCents={grandTotalInCents}
          tipAmountInCents={tipAmountInCents}
          onDone={(donationAmount) => {
            donationAmountInCents.current = donationAmount
            if (signatureRequired) {
              setIsReady(true)
            } else {
              onNext()
            }
          }}
          onBack={() => {
            setDonating(false)
            donationAmountInCents.current = 0
          }}
        />
        {customTipModalElement}
        {confirmDefaultTipModalElement}
      </>
    )
  }

  const onTipClick = (tip) => {
    if (!tip?.selected) {
      setSelectionIsDefault(false)
    }

    if (tip?.isCustom) {
      setSelectedTipAmount(0) // don't let default value to be previous tip amount
      setShowCustomTipModal(true)
      return
    }

    setSelectedTipAmount(tip?.amount)
  }

  const getTipSelectedFromTipAmount = (tipAmount) => {
    let index = findIndex(tipSuggestions, (tip) => tip.amountInCents === tipAmount)

    // if -1 then the tip amount is not in the suggestions, meaning it is a custom tip
    if (index === -1) {
      index = findIndex(tipSuggestions, (tip) => tip.custom === true)
    }

    return index
  }

  const displayTipSuggestions = () => {
    let defaultSelected = false
    let defaultIndex = -1

    if (selectedTipAmount > 0) {
      defaultIndex = getTipSelectedFromTipAmount(selectedTipAmount)
    }

    if (!!signatureDefaults?.selectedTipAmount) {
      defaultIndex = getTipSelectedFromTipAmount(signatureDefaults?.selectedTipAmount)
    }

    if (isMobile && !isEmpty(tipSuggestions)) {
      let tipDisplayArray = [[], []]

      tipSuggestions.forEach((tip, index) => {
        defaultSelected = defaultSelected || (defaultIndex === index ?? tip?.selected)

        if (tip.tipFree || tip?.custom) {
          const tipObject = {
            id: tip?.display,
            title: tip?.display,
            amount: tip?.amountInCents,
            isCustom: tip?.custom,
            style: styles?.bottomRowTipSuggestions,
            selected: defaultIndex === index ?? tip?.selected,
          }

          if (tip?.custom && tipDisplayArray[1].length === 1) {
            tipDisplayArray[1].unshift(tipObject)
            return
          }

          tipDisplayArray[1].push(tipObject)
          return
        }

        tipDisplayArray[0].push({
          id: tip?.display,
          title: tip?.display,
          amount: tip?.amountInCents,
          isCustom: tip?.custom,
          style: styles?.topRowTipSuggestions,
          selected: defaultIndex === index ?? tip?.selected,
        })
      })

      return (
        <Box className={styles.tipContainer}>
          <VNButtonContainer
            containerStyle={styles.buttonContainer}
            buttonStyle={styles.containerButton}
            onClick={onTipClick}
            defaultSelected={defaultSelected}
            buttons={tipDisplayArray}
          />

          <Box className={styles.totalContainer}>
            <VNTotalListItem
              title={`Subtotal (${remoteOrderTotal?.lineItems?.length ?? order?.lineItems?.length ?? itemCount})`}
              amount={centsToDollars(subtotalInCents)}
            />
            {remoteOrderTotal?.promotions?.length > 0 &&
              remoteOrderTotal?.promotions?.map((promotion, index) => (
                <VNTotalListItem
                  key={index}
                  title={promotion?.name}
                  amount={centsToDollars(promotion?.discountAmountInCents)}
                  isHighlighted={true}
                />
              ))}
            {remoteOrderTotal?.serviceChargeInCents > 0 && (
              <VNTotalListItem
                titleIcon={serviceChargeLegal && <Info style={{ marginLeft: 8, color: 'white' }} onClick={() => {}} />}
                title={remoteOrderTotal?.serviceChargeName || 'Service Charge'}
                amount={remoteOrderTotal?.serviceChargeDisplay}
              />
            )}

            {(taxExempt || order?.taxExempt) && <VNTotalListItem title={'Tax'} amount={'Exempt'} />}

            {Object.keys(taxRulesBreakdown)?.length > 0 &&
              Object.keys(taxRulesBreakdown).map((taxRule, index) => (
                <VNTotalListItem
                  key={index}
                  title={taxRulesBreakdown[taxRule]?.taxRuleName}
                  amount={centsToDollars(taxRulesBreakdown[taxRule]?.taxRuleAmountInCents)}
                />
              ))}
            {selectedTipAmount > 0 && <VNTotalListItem title={'Tip'} amount={centsToDollars(selectedTipAmount)} />}
          </Box>
        </Box>
      )
    }

    if (isDoneLoading && !isEmpty(tipSuggestions)) {
      return (
        <SegmentedControl
          items={tipSuggestionItems}
          selectedIndex={selectedTipSuggestionIndex}
          onChange={selectedTipSuggestionDidChange}
          isKiosk={isKiosk}
        />
      )
    }
    return null
  }

  const onNextButtonClick = (_, tipConfirmed = false) => {
    if (order?.wasCreatedInStadium && !networkAvailable && isTabOrder) {
      dispatch(setConnectionRequiredDialog({ open: true, subtext: APPLICATION_STRINGS.INTERNET_CONNECTION_REQUIRED }))

      const orderId = order?.uuid || orderUuid
      dispatch(orderDetailsRequested(orderId))
      dispatch(selectOrder(orderId))

      history.push('/orders')
      return
    }

    // show confirm tip modal before going to next screen
    if (!tipConfirmed && selectionIsDefault && initialTipPercentage && confirmDefaultTipEnabled) {
      setConfirmDefaultTipModal(true)
      return
    }

    if (shouldShowDonation) {
      if (signatureRequired) {
        completeSignature(false)
        doLinesMatch()
      }
      setDonating(true)
      return
    }

    onNext()
  }

  const signatureDisplay = () => {
    if (isMobile) {
      return
    }

    if (signatureRequired && isEmpty(tipSuggestions)) {
      ;<span className={styles.noTipMessage}>Please sign below</span>
    }

    return (
      <GratuityHeader
        className={cn(styles.gratuityHeader, { [styles.pop]: shouldAnimate })}
        primaryText={!isMobile && primaryText}
        secondaryText={isEmpty(tipSuggestions) ? <span className={styles.noTipMessage}>Please sign below</span> : secondaryText}
      />
    )
  }

  const displayNavBar = () => {
    if (isKiosk) {
      return (
        <>
          {!kioskOfflineEnabled && <OfflineModal />}
          <div className={styles.gratuityHeaderKiosk}>
            <BackButton
              handleKeyDown={handleKeyDown}
              ariaLabel={primaryText}
              refProp={backButtonRef}
              className={styles.backButton}
              withIcon={true}
              onClick={goBack}
              iconStyle={styles.backButtonIcon}
            >
              <div className={styles.orderTotal}>{primaryText}</div>
            </BackButton>
            {!isEmpty(tipSuggestions) && <span>{secondaryTextKiosk}</span>}
          </div>
        </>
      )
    }

    return (
      <>
        <VNConcessionsNavBar onClick={goBack} textDisplay={isMobile && `${centsToDollars(tipAmountInCents)} tip`} />
        {signatureDisplay()}
      </>
    )
  }

  const getNextButtonText = () => {
    if (!isMobile) {
      return 'Next'
    }

    return `Pay ${primaryText}`
  }

  return isDoneLoading ? (
    <div className={cn(classNames)}>
      {displayNavBar()}
      <div className={styles.contentContainer} style={containerStyle}>
        {displayTipSuggestions()}
        {isKiosk && isDoneLoading && signatureRequired && isEmpty(tipSuggestions) && (
          <span className={styles.noTipMessage}>PLEASE SIGN BELOW</span>
        )}
        {isDoneLoading && signatureRequired && (
          <>
            <div
              className={styles.signaturePadContainer}
              onTouchEnd={() => setIsSignatureValid(true)}
              onMouseUp={() => setIsSignatureValid(true)}
            >
              <CanvasDraw
                canvasWidth={canvasWidth}
                canvasHeight={getResponsiveCanvasHeight()}
                className={styles.signaturePad}
                brushRadius={1.2}
                lazyRadius={0}
                brushColor={isKiosk ? '#000000' : '#ffffff'}
                backgroundColor="transparent"
                catenaryColor="transparent"
                hideGrid
                ref={signaturePadRef}
              />

              {isKiosk && (
                <>
                  <div className={styles.signHereContainer}>
                    <SignatureIcon />
                    <div className={styles.signHereKiosk} />
                  </div>
                  <p className={styles.disclaimerKiosk}>
                    I agree to pay the above total amount according to my card issuer agreement
                  </p>
                </>
              )}
            </div>
            {!isKiosk && (
              <div className={styles.signHereContainer}>
                <SignatureIcon />
                <span className={styles.signHere} />
              </div>
            )}
          </>
        )}
        {!isKiosk && isDoneLoading && signatureRequired && (
          <p className={styles.disclaimer}>I agree to pay the above total amount according to my card issuer agreement.</p>
        )}
        {isKiosk && isDoneLoading && (!isEmpty(tipSuggestions) || signatureRequired) && (
          <div className={styles.clearButtonContainer}>
            {signatureRequired && (
              <Button onClick={clearSignature} className={styles.clearButtonKiosk}>
                CLEAR SIGNATURE
              </Button>
            )}
            <Button disabled={!isFormValid} onClick={onNextButtonClick} className={styles.completeButtonKiosk}>
              Next
            </Button>
          </div>
        )}
        {!isKiosk && (
          <div className={styles.nextButtonContainer}>
            <VNBottomButton
              disabled={!isFormValid || !isDoneLoading}
              onClick={onNextButtonClick}
              text={getNextButtonText()}
              className={styles.nextButton}
            />
          </div>
        )}
      </div>
      {customTipModalElement}
      {confirmDefaultTipModalElement}
    </div>
  ) : isKiosk ? (
    <ConnectingIndicator isKiosk={true} />
  ) : (
    <VNLoader />
  )
}

const mapStateToProps = (state, ownProps) => {
  const offlineTotal = getTotal(state)
  const itemCount = getCartItemsCountWithQuantities(state)
  const isKiosk = getIsKiosk(state)
  // used for kiosk payments and round up for charity.
  const skipTips = ownProps?.history?.location?.state?.skipTips ?? false
  // when navigating back to the signature screen we want to show the charity screen first.
  const kioskPayment = getKioskPayment(state)
  const paymentId = ownProps?.match?.params?.paymentId
  const tenderAmountInCents = ownProps?.history?.location?.state?.tenderAmountInCents ?? 0
  const addTipThenRedirectPath = ownProps?.history?.location?.state?.addTipThenRedirectPath ?? ''
  const closeWithSavedCard = ownProps?.history?.location?.state?.closeWithSavedCard ?? false
  const customTender = ownProps?.history?.location?.state?.customTender ?? {}
  const maximumAmount = ownProps?.history?.location?.state?.maximumAmount ?? null
  const order = getOrderInProgress(state)
  const menu = makeGetCurrentMenu()(state)
  const serviceChargeLegal = makeGetServiceChargeLegalText()(state)
  const signatureDefaults = getSignatureDefaults(state)
  const showCharityFirst = signatureDefaults?.shouldShowCharityFirst ?? false
  const menuTipSuggestions = order?.hasTipApplied && !addTipThenRedirectPath ? [] : (menu?.tipSuggestions ?? [])
  let maxGratuity = Infinity
  const confirmDefaultTipEnabled = getDefaultTipEnabled(state)

  return {
    skipTips,
    signatureDefaults,
    showCharityFirst,
    offlineTotal,
    itemCount,
    menuTipSuggestions,
    maxGratuity,
    isKiosk,
    kioskPayment,
    paymentId,
    tenderAmountInCents,
    addTipThenRedirectPath,
    order,
    confirmDefaultTipEnabled,
    customTender,
    closeWithSavedCard,
    maximumAmount,
    serviceChargeLegal,
  }
}

const mapDispatchToProps = (dispatch, props) => {
  const goBack = props?.history?.goBack ?? (() => {})
  const push = props?.history?.push ?? (() => {})
  const addTipThenRedirectPath = props?.history?.location?.state?.addTipThenRedirectPath ?? ''
  const tenderAmountInCents = props?.history?.location?.state?.tenderAmountInCents ?? 0
  const closeWithSavedCard = props?.history?.location?.state?.closeWithSavedCard ?? false

  return {
    goPrevScreen: (order) => {
      goBack()

      /* For reopened orders closed to a saved card, remove payments from order, these payments are added thru
       the addReopenPayment action only and are readded before hitting this screen so we need to delete them
       in case of going back to the previous screen
      */
      if (order.reopened && closeWithSavedCard) {
        dispatch(removePaymentsFromOrderInProgress())
      }

      if (!addTipThenRedirectPath) return

      if (!isTabbed(order)) {
        dispatch(clearOrderInProgress())
        return
      }

      dispatch(clearTipOrderInProgress())
      dispatch(clearTipFromOrder(order.uuid))
      dispatch(clearDonationOrderInProgress())
      dispatch(clearDonationFromOrder(order.uuid))
    },
    addSignatureDefaults: (tipIndex, tipAmount, shouldShowCharityFirst, signature) => {
      dispatch(setSignatureDefaults(tipIndex, tipAmount, shouldShowCharityFirst, signature))
    },
    setKioskPayment: (paymentType) => {
      dispatch(setKioskPayment(paymentType))
    },
    addDonationToOrderInProgress: (donationAmountInCents) => {
      dispatch(addDonationsToOrderInProgress(donationAmountInCents))
    },
    addTipToOrderInProgress: (tipAmountInCents, addTipThenRedirectPath) => {
      dispatch(addTipOrderInProgress(tipAmountInCents))
      push(addTipThenRedirectPath)
    },
    addPaymentToOrder: ({
      customTender,
      tipAmountInCents,
      tenderAmountInCents,
      paymentId,
      maximumAmount,
      signature,
      charityAmountInCents,
    }) => {
      dispatch(
        addPaymentToOrderInProgress({
          tenderAmountInCents,
          paymentId,
          paymentType: customTender.name,
          shortDescription: customTender.displayName,
          tipAmountInCents,
          maximumAmount,
          signature,
          charityAmountInCents,
        }),
      )
    },
    closeNoOpTender: ({
      order,
      orderUuid,
      customTender,
      tipAmountInCents,
      tenderAmountInCents,
      paymentId,
      redirectOnSync,
      charityAmountInCents,
    }) => {
      if (isEmpty(order)) {
        dispatch(closeNoOpTender({ orderUuid, tender: customTender, tipAmountInCents, redirectOnSync, charityAmountInCents }))
      } else {
        dispatch(
          addPaymentToOrderInProgress({
            tenderAmountInCents,
            paymentId,
            paymentType: customTender.name,
            shortDescription: customTender.displayName,
            tipAmountInCents,
            charityAmountInCents,
          }),
        )
      }
    },
    addPaymentToSavedCard: (tipAmountInCents, charityAmountInCents, signature) => {
      dispatch(updateSavedPayment({ tipAmountInCents, charityAmountInCents, signature, tenderAmountInCents }))
    },
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Signature))
